import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CellRenderer, CellRendererParams } from '../../core/interfaces/cell-renderer.interface';

/**
 * Expired Days Cell Renderer Component
 *
 * Calculates and displays the number of days a document is expired.
 * Shows in red color when days > 0.
 *
 * Features:
 * - Calculates days between expiration date and today
 * - Shows red text when expired (days > 0)
 * - Right-aligned text
 * - Falls back to "0" for null/undefined/invalid dates
 *
 * Usage in ColDef:
 * ```typescript
 * {
 *   colId: 'availableDays',
 *   headerName: 'Días Vencidos',
 *   cellRenderer: ExpiredDaysCellRendererComponent,
 *   valueGetter: (row) => row.expirationDate,
 *   cellClass: 'text-right'
 * }
 * ```
 *
 * Output examples:
 * - "0" (black) - Not expired
 * - "15" (red) - 15 days expired
 */
@Component({
  selector: 'app-expired-days-cell-renderer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      class="text-content pr-14"
      [class.text-red-600]="expiredDays > 0"
      [class.dark:text-red-400]="expiredDays > 0"
    >
      {{ expiredDays }}
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }
      .text-red-600 {
        color: rgb(220, 38, 38);
      }
      :host-context(.dark) .dark\:text-red-400 {
        color: rgb(248, 113, 113);
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExpiredDaysCellRendererComponent implements CellRenderer<unknown, Date | string> {
  /**
   * Parameters from the grid containing the cell value (expiration date)
   */
  @Input() params!: CellRendererParams<unknown, Date | string>;

  /**
   * Calculate expired days from expiration date
   */
  get expiredDays(): number {
    const expirationDate = this.params?.value;

    if (!expirationDate) return 0;

    const today = new Date();
    const expiration = new Date(expirationDate);

    // Set hours to 0 for accurate day comparison
    today.setHours(0, 0, 0, 0);
    expiration.setHours(0, 0, 0, 0);

    // Check if date is valid
    if (isNaN(expiration.getTime())) return 0;

    // Calculate difference in days
    const timeDifference = today.getTime() - expiration.getTime();
    const dayDifference = timeDifference / (1000 * 3600 * 24);

    // Return 0 if not expired, otherwise return number of days
    return dayDifference > 0 ? Math.floor(dayDifference) : 0;
  }
}
